import React, { useEffect, useRef } from "react";
import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import styled from "@emotion/styled";

import { inputStyles } from "~/components/FormElements/Input";
import IconOnlyButton from "~/components/buttons/IconOnlyButton";

import useStringTranslation from "~/hooks/translations/useStringTranslation";

import { useLocationContext } from "~/context/LocationContext";

const Form = styled.form`
    position: relative;
    display: flex;
    margin: 0 ${({ isModal }) => (isModal ? "1rem" : "0")};
    border-radius: 100px;
    overflow: hidden;
    border: 2px solid transparent;

    &:focus-within {
        border: 2px solid ${({ theme }) => theme.palette.purple};
    }
`;

const SubmitButton = styled(IconOnlyButton)`
    ${inputStyles};

    border: 0;
    width: max-content;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 4rem 0 1rem;
    margin-left: 0;

    &:focus,
    &:focus-visible {
        border: 0;
        border-left: 2px solid ${({ theme }) => theme.palette.purple};
        outline: none;
    }
`;

const Input = styled.input`
    ${inputStyles};
    margin-top: 0;
    margin-bottom: 0;

    border: 0;
    padding: 2rem 0 2rem 4rem;

    &:focus,
    &:focus-visible {
        border: 0;
    }

    &::-webkit-input-placeholder {
        font-weight: 500;
    }

    &::-moz-placeholder {
        font-weight: 500;
    }

    &::-ms-input-placeholder {
        font-weight: 500;
    }
`;

const ClearInputButton = styled(IconOnlyButton)`
    ${inputStyles};
    display: flex;
    width: max-content;
    border: 2px solid transparent;
    font-size: 2.3rem;
    align-items: center;
    margin-left: 0;
    margin-right: 0;

    &::after {
        content: "";
        height: 100%;
        border: 1px solid ${({ theme }) => theme.palette.graySix};
        margin-left: 3rem;
    }

    &:focus,
    &:focus-visible {
        border: 0;
        border-left: 2px solid ${({ theme }) => theme.palette.purple};
        border-right: 2px solid ${({ theme }) => theme.palette.purple};
        padding: 0 2.5rem;
        outline: none;

        &::after {
            display: none;
        }
    }
`;

const AutocompleteInput = ({
    id,
    closeMenu,
    inputValue,
    getInputProps,
    updateParent = () => {},
    clearInput,
    placeholder = "",
    isModal,
    resetParentState = null,
    setState,
    queryParam = "",
}) => {
    const { location } = useLocationContext();
    const inputRef = useRef(null);

    const { clearText } = useStringTranslation([
        { text: "Clear", handle: "Clear text" },
    ]);

    useEffect(() => {
        if (isModal && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isModal, inputRef]);

    useEffect(() => {
        const isHeaderNav = location?.state?.isHeaderNav ?? false;

        if (isHeaderNav) {
            setState({
                selectedItem: { title: queryParam },
                inputValue: queryParam,
            });
            location.state.isHeaderNav = false;
        }
    }, [queryParam, location?.state, setState]);

    return (
        <Form
            isModal={isModal}
            onSubmit={(e) => {
                e.preventDefault();
                updateParent(inputValue);
                closeMenu();
            }}
        >
            <Input
                placeholder={placeholder}
                ref={inputRef}
                id={id}
                {...getInputProps()}
            />
            {!isEmpty(inputValue) ? (
                <ClearInputButton
                    screenReaderText={clearText}
                    icon="xmark"
                    iconLibrary="fas"
                    type="button"
                    onClick={() => {
                        clearInput();
                        if (resetParentState) {
                            resetParentState();
                        }
                        inputRef.current.focus();
                    }}
                />
            ) : null}
            <SubmitButton
                screenReaderText="Submit"
                icon="search"
                iconLibrary="fas"
                type="submit"
            />
        </Form>
    );
};

AutocompleteInput.propTypes = {
    queryParam: PropTypes.string,
    resetParentState: PropTypes.func,
    updateParent: PropTypes.func,
    placeholder: PropTypes.string,
    isModal: PropTypes.bool.isRequired,
    inputValue: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    closeMenu: PropTypes.func.isRequired,
    getInputProps: PropTypes.func.isRequired,
    clearInput: PropTypes.func.isRequired,
    setState: PropTypes.func.isRequired,
};

export default AutocompleteInput;
